/**
 * The Association seal, drawn as plain inline SVG.
 *
 * A seal is an optional tradition. Some Associations keep one, many do not.
 * It is a mark of care on a page and nothing more.
 */

export interface SealStyle {
  /** Optional year the Association was established, shown around the bottom. */
  established_year?: string;
  /** Optional state, used around the bottom when no year is given. */
  state?: string;
}

export const SEAL_CAPTION = 'Seal of the Association';

export const SEAL_CHECKBOX_LABEL =
  "Apply the Association's seal (sealed at discretion, as your records tradition provides)";

export const SEAL_SETTINGS_COPY =
  'A seal is optional tradition. Some Associations keep one; many do not. Yours applies only where you choose.';

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Keeps arched text from running off the ring on very long names. */
function fitArch(text: string, max: number): string {
  const clean = text.trim().toUpperCase();
  return clean.length > max ? `${clean.slice(0, max - 1).trim()}\u2026` : clean;
}

/**
 * Renders a dignified circular seal: a double ring, the Association name arched
 * around the top, the year or the state around the bottom, and a small mark at
 * the center. Serif, black only, prints cleanly.
 */
export function buildSealSvg(associationName: string, style?: SealStyle | null): string {
  const name = fitArch(associationName || 'The Association', 46);
  const yearText = style?.established_year ? `ESTABLISHED ${style.established_year}` : '';
  const bottom = fitArch(yearText || style?.state || '', 34);

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200" role="img" aria-label="${esc(
    associationName,
  )} seal">
  <defs>
    <path id="sealArchTop" d="M 30 100 A 70 70 0 0 1 170 100" fill="none" />
    <path id="sealArchBottom" d="M 36 100 A 64 64 0 0 0 164 100" fill="none" />
  </defs>
  <circle cx="100" cy="100" r="92" fill="none" stroke="#1a1a1a" stroke-width="2.5" />
  <circle cx="100" cy="100" r="82" fill="none" stroke="#1a1a1a" stroke-width="1" />
  <text font-family="Cambria, Georgia, serif" font-size="13" letter-spacing="2.2" fill="#1a1a1a">
    <textPath href="#sealArchTop" startOffset="50%" text-anchor="middle">${esc(name)}</textPath>
  </text>
  ${
    bottom
      ? `<text font-family="Cambria, Georgia, serif" font-size="11" letter-spacing="2" fill="#1a1a1a">
    <textPath href="#sealArchBottom" startOffset="50%" text-anchor="middle">${esc(bottom)}</textPath>
  </text>`
      : ''
  }
  <path d="M 100 74 L 106 92 L 125 92 L 110 103 L 116 121 L 100 110 L 84 121 L 90 103 L 75 92 L 94 92 Z"
        fill="none" stroke="#1a1a1a" stroke-width="1.4" stroke-linejoin="round" />
</svg>`;
}

export interface SealContext {
  /** True only when the Association keeps a seal and the steward applied it. */
  sealEnabled?: boolean;
  /** The rendered seal markup. */
  sealSvg?: string;
}

/** The seal as it sits on a page, at a modest size, with its small caption. */
export function sealBlockHtml(ctx: SealContext): string {
  if (!ctx?.sealEnabled || !ctx?.sealSvg) return '';
  return `<div class="seal-mark">${ctx.sealSvg}<div class="seal-caption">${SEAL_CAPTION}</div></div>`;
}

/** Shared page CSS for the seal mark. */
export const SEAL_STYLE_CSS = `
  .seal-mark { margin-top: 22px; width: 1.2in; }
  .seal-mark svg { width: 1.2in; height: 1.2in; }
  .seal-caption { font-size: 8pt; font-style: italic; color: #555; text-align: center; margin-top: 4px; }
`;
